'use client'

import * as React from 'react'
import { Plus, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { InstitutionLogo } from '@/components/encuentro/frame/institution-logo'
import { accentFor } from '@/lib/design/accent'
import { isHexColor, rgbToHex } from '@/lib/design/contrast'
import type { LogoOption, SuggestedGroup, Suggestion } from '@/lib/institution-lookup/types'
import { SUGGESTED_GROUP_KINDS } from '@/lib/institution-lookup/types'
import { cn } from '@/lib/utils'

import { SELECT_CLASSES } from '@/components/encuentro/screens/profile/edit/form-controls'

/**
 * How sure the lookup is about a field and where it came from, under the
 * field. A low-confidence field is highlighted so it gets a second look.
 */
export function SuggestionMeta({ suggestion }: { suggestion: Suggestion<unknown> | null }) {
  const t = useTranslations('encuentro.institutionWizard.confidence')
  if (!suggestion) return null
  const low = suggestion.confidence === 'low'
  return (
    <p
      data-confidence={suggestion.confidence}
      className={cn(
        'flex flex-wrap items-center gap-2 text-en-meta',
        low ? 'text-en-warning' : 'text-en-ink-muted'
      )}
    >
      <span
        className={cn(
          'rounded-en-sm px-2 py-px text-en-caption',
          low ? 'bg-en-warning-soft' : 'bg-en-surface-sunken'
        )}
      >
        {t(suggestion.confidence)}
      </span>
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {t('source', { source: suggestion.source })}
      </span>
    </p>
  )
}

/** A field wrapper that tints a low-confidence suggestion. */
export function Suggested({
  suggestion,
  children,
  className,
}: {
  suggestion: Suggestion<unknown> | null
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      data-low={suggestion?.confidence === 'low' ? 'true' : undefined}
      className={cn(
        'grid min-w-0 content-start gap-2',
        suggestion?.confidence === 'low' && 'rounded-en-md bg-en-warning-soft p-3',
        className
      )}
    >
      {children}
      <SuggestionMeta suggestion={suggestion} />
    </div>
  )
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  help,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
  help?: string
}) {
  const id = React.useId()
  return (
    <div className="grid min-w-0 content-start gap-2 text-en-ink">
      <label htmlFor={id} className="text-en-label">
        {label}
      </label>
      {help && <p className="text-en-meta text-en-ink-muted">{help}</p>}
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={SELECT_CLASSES}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Up to three logos, each on a light and a dark ground, plus "none". */
export function LogoPicker({
  logos,
  value,
  onChange,
}: {
  logos: readonly LogoOption[]
  value: number | null
  onChange: (index: number | null) => void
}) {
  const t = useTranslations('encuentro.institutionWizard.identity')
  const name = React.useId()
  const choices: Array<{ index: number | null; logo: LogoOption | null }> = [
    ...logos.map((logo, index) => ({ index, logo })),
    { index: null, logo: null },
  ]
  return (
    <fieldset className="grid min-w-0 gap-2">
      <legend className="mb-2 text-en-label">{t('logo')}</legend>
      <p className="text-en-meta text-en-ink-muted">{t('logoHelp')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {choices.map(({ index, logo }) => {
          const selected = value === index
          return (
            <label
              key={index ?? 'none'}
              className={cn(
                'grid cursor-pointer gap-3 rounded-en-md border p-3',
                selected
                  ? 'border-en-ink bg-en-surface-sunken'
                  : 'border-en-line-control hover:bg-en-surface-sunken'
              )}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name={name}
                  checked={selected}
                  onChange={() => onChange(index)}
                  className="size-5 shrink-0 cursor-pointer accent-en-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
                />
                <span className="text-en-label">{logo ? logo.source : t('logoNone')}</span>
              </span>
              {logo && (
                <span className="grid grid-cols-2 overflow-hidden rounded-en-sm border border-en-line">
                  <span
                    data-theme="light"
                    className="grid place-items-center bg-en-bg p-3"
                    title={t('onLight')}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- a data: URL made on our server */}
                    <img src={logo.preview} alt={t('onLight')} className="size-16 object-contain" />
                  </span>
                  <span
                    data-theme="dark"
                    className="grid place-items-center bg-en-bg p-3"
                    title={t('onDark')}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- a data: URL made on our server */}
                    <img src={logo.preview} alt={t('onDark')} className="size-16 object-contain" />
                  </span>
                </span>
              )}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

/** The colour, its swatch and the contrast check against the Encuentro grounds. */
export function ColourField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const t = useTranslations('encuentro.institutionWizard.identity')
  const id = React.useId()
  const valid = isHexColor(value) && value.length === 7
  const check = valid ? accentFor(value, 'light') : null
  return (
    <div className="grid min-w-0 content-start gap-2 text-en-ink">
      <label htmlFor={id} className="text-en-label">
        {t('colour')}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          aria-label={t('colourPicker')}
          value={valid ? value : rgbToHex([0, 0, 0])}
          onChange={(event) => onChange(event.target.value)}
          className="size-10 shrink-0 cursor-pointer rounded-en-sm border border-en-line-control bg-en-surface p-1"
        />
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value.trim())}
          placeholder={t('colourPlaceholder')}
          spellCheck={false}
          className="min-h-en-control w-32 rounded-en-md border border-en-line-control bg-en-surface px-3 font-en-sans text-en-body text-en-ink"
        />
        {check && (
          <span
            aria-hidden
            className="h-10 min-w-0 flex-1 rounded-en-sm border border-en-line"
            style={{ backgroundColor: check.accent }}
          />
        )}
      </div>
      {check && (
        <p
          className={cn(
            'flex flex-wrap items-center gap-2 text-en-meta',
            check.adjusted ? 'text-en-warning' : 'text-en-positive'
          )}
        >
          {check.adjusted ? (
            <>
              <span>{t('colourAdjusted', { readable: check.accentText })}</span>
              <Button
                look="encuentro"
                variant="secondary"
                size="sm"
                onClick={() => onChange(check.accentText)}
              >
                {t('useReadable')}
              </Button>
            </>
          ) : (
            t('colourOk')
          )}
        </p>
      )}
    </div>
  )
}

export function GroupsEditor({
  groups,
  onChange,
}: {
  groups: SuggestedGroup[]
  onChange: (groups: SuggestedGroup[]) => void
}) {
  const t = useTranslations('encuentro.institutionWizard.groups')
  const update = (i: number, patch: Partial<SuggestedGroup>) =>
    onChange(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)))
  return (
    <div className="grid gap-3">
      <p className="text-en-meta text-en-ink-muted">{t('help')}</p>
      <ul className="grid gap-2">
        {groups.map((group, i) => (
          <li key={i} className="flex min-w-0 flex-wrap items-center gap-2">
            <input
              aria-label={t('name')}
              value={group.name}
              maxLength={80}
              onChange={(event) => update(i, { name: event.target.value })}
              className="min-h-en-control min-w-0 flex-1 rounded-en-md border border-en-line-control bg-en-surface px-3 font-en-sans text-en-body text-en-ink"
            />
            <select
              aria-label={t('kind')}
              value={group.kind}
              onChange={(event) =>
                update(i, { kind: event.target.value as SuggestedGroup['kind'] })
              }
              className={cn(SELECT_CLASSES, 'w-auto')}
            >
              {SUGGESTED_GROUP_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`kinds.${kind}`)}
                </option>
              ))}
            </select>
            <Button
              look="encuentro"
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={t('remove', { name: group.name })}
              onClick={() => onChange(groups.filter((_, j) => j !== i))}
            >
              <X aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      {groups.length < 12 && (
        <div>
          <Button
            look="encuentro"
            variant="secondary"
            size="sm"
            onClick={() => onChange([...groups, { name: '', kind: 'faculty' }])}
          >
            <Plus aria-hidden />
            {t('add')}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * The top of the sidebar as the new institution will see it: its logo, the
 * short name and the colour, on light and dark.
 */
export function BrandPreview({
  name,
  shortName,
  logo,
  colour,
}: {
  name: string
  shortName: string
  logo: string | null
  colour: string
}) {
  const t = useTranslations('encuentro.institutionWizard.preview')
  const shown = shortName.trim() || name.trim() || t('placeholder')
  const valid = isHexColor(colour) && colour.length === 7
  return (
    <section aria-label={t('title')} className="grid gap-2">
      <h3 className="text-en-label">{t('title')}</h3>
      {(['light', 'dark'] as const).map((theme) => {
        const accent = valid ? accentFor(colour, theme) : null
        return (
          <div
            key={theme}
            data-theme={theme}
            style={
              accent
                ? ({
                    '--en-accent': accent.accent,
                    '--en-accent-text': accent.accentText,
                    '--en-accent-soft': accent.accentSoft,
                  } as React.CSSProperties)
                : undefined
            }
            className="grid gap-3 rounded-en-md bg-en-canvas p-3"
          >
            <div className="flex min-h-13 min-w-0 items-center gap-3 rounded-en-md bg-en-surface px-2 text-en-ink shadow-en-card">
              <InstitutionLogo url={logo} />
              <span className="grid min-w-0 flex-1">
                <span className="line-clamp-2 break-words text-en-body-strong">{shown}</span>
                <span className="truncate text-en-meta text-en-ink-muted">{t('role')}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-en-md bg-en-accent-soft px-3 py-2">
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-en-full bg-en-accent ring-1 ring-en-ink-muted"
              />
              <span className="text-en-label text-en-accent-text">{t('accent')}</span>
            </div>
          </div>
        )
      })}
    </section>
  )
}
